import { useEffect, useState } from "react";
import type { EffectiveServiceOffering } from "@/lib/service/types";
import {
  listServiceOfferingDocuments,
  offeringDocumentLinkLabel,
} from "@/lib/service/offeringDocumentsClient";
import type { ServiceOfferingDocument } from "@/lib/service/types";
import { shouldShowOpportunityOfferingGuidancePanel } from "@/lib/opportunity/opportunityOfferingGuidance";
import { openServiceBoard, openServiceSetup } from "@/lib/workspaceModeNavigation";

export function OpportunityOfferingGuidancePanel(props: {
  matchedOffering: EffectiveServiceOffering | null;
  onOpenDocumentsTab?: () => void;
}) {
  const { matchedOffering, onOpenDocumentsTab } = props;
  const [documents, setDocuments] = useState<ServiceOfferingDocument[]>([]);
  const [publisherOrgName, setPublisherOrgName] = useState<string | null>(null);
  const [isInherited, setIsInherited] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!matchedOffering) {
      setDocuments([]);
      setPublisherOrgName(null);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    void listServiceOfferingDocuments({ offering_id: matchedOffering.id, sign: true }).then((res) => {
      if (cancelled) return;
      setLoaded(true);
      if (res.ok) {
        setDocuments(res.data.documents);
        setPublisherOrgName(res.data.publisher_org_name);
        setIsInherited(res.data.is_inherited);
      } else {
        setDocuments([]);
        setPublisherOrgName(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [matchedOffering?.id]);

  if (!shouldShowOpportunityOfferingGuidancePanel(matchedOffering, loaded, documents.length)) {
    return null;
  }

  const offering = matchedOffering!;

  return (
    <div
      className="rounded-xl border border-indigo-200 bg-indigo-50/80 px-4 py-3 text-sm text-indigo-950 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-100"
      data-testid="opportunity-offering-guidance-panel"
    >
      <p className="font-medium">{offering.label} — pricing &amp; order forms</p>
      <p className="mt-1 text-xs text-indigo-900/85 dark:text-indigo-100/85">
        Download the worksheet or order form, complete pricing off-app, then enter the subtotal below.
        Upload completed worksheet to{" "}
        {onOpenDocumentsTab ? (
          <button
            type="button"
            onClick={onOpenDocumentsTab}
            className="font-semibold underline underline-offset-2 hover:text-indigo-700 dark:hover:text-indigo-200"
          >
            Documents tab
          </button>
        ) : (
          "Documents tab"
        )}
        .
      </p>
      {offering.is_inherited && publisherOrgName ? (
        <p className="mt-1 text-[11px] text-indigo-800/75 dark:text-indigo-200/75">
          Forms from {publisherOrgName} — download links include publisher label.
        </p>
      ) : null}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {documents.map((doc) => (
          <li key={doc.id} className="inline-flex items-center gap-1.5">
            <span className="text-indigo-800/70 dark:text-indigo-200/70">
              {offeringDocumentLinkLabel(doc, {
                publisherOrgName,
                isInherited: isInherited || offering.is_inherited,
                compact: true,
              })}
              :
            </span>
            {doc.signed_url ? (
              <>
                <a
                  href={doc.signed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-2 hover:text-indigo-700 dark:hover:text-indigo-200"
                >
                  Download
                </a>
                <button
                  type="button"
                  className="text-indigo-800/80 hover:underline dark:text-indigo-200/80"
                  onClick={() => {
                    if (doc.signed_url) window.open(doc.signed_url, "_blank", "noopener");
                  }}
                >
                  Print
                </button>
              </>
            ) : (
              <span>{doc.filename}</span>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
        <button
          type="button"
          onClick={() => openServiceSetup({ offeringId: offering.id })}
          className="text-indigo-800 underline underline-offset-2 hover:text-indigo-950 dark:text-indigo-300 dark:hover:text-indigo-100"
        >
          Open Service setup →
        </button>
        <button
          type="button"
          onClick={openServiceBoard}
          className="text-indigo-800 underline underline-offset-2 hover:text-indigo-950 dark:text-indigo-300 dark:hover:text-indigo-100"
        >
          Open Service board →
        </button>
      </div>
    </div>
  );
}
